import { http } from "../http";
import dayjs from "dayjs";
import qs from "qs";

export const getTrainingOrganizations = async () => {
  try {
    const response = await http.get(`/api/ohioapproved/training_org`);
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const getEventsByTrainingOrgId = async (
  trainingOrgId,
  pageNumber,
  pageSize,
  sortBy,
  isDesc,
  filterModel,
  archiveFilter,
) => {
  let baseUrl =
    `/api/ohioapproved/training_org_trainings/${trainingOrgId}` +
    `?page=${pageNumber}` +
    `&pageSize=${pageSize}` +
    `&sortBy=${sortBy}` +
    `&sortDesc=${isDesc}`;

  const columnFilters = [];
  if (filterModel?.items?.length > 0) {
    for (const item of filterModel.items) {
      columnFilters.push({
        id: item.field,
        value: item.value,
      });
    }
  }

  /**
   * Archive filters
   * active   => archived = 0
   * archived => archived = 1
   * all      => no filter
   */
  if (archiveFilter === "active") {
    columnFilters.push({
      id: "archived",
      value: "0",
    });
  }

  if (archiveFilter === "archived") {
    columnFilters.push({
      id: "archived",
      value: "1",
    });
  }

  if (columnFilters.length > 0) {
    baseUrl += `&columnFilters=${encodeURIComponent(
      JSON.stringify(columnFilters),
    )}`;
  }

  try {
    const response = await http.get(baseUrl);
    return response.data;
  } catch (ex) {
    console.error(ex);
  }
};

export const searchByInstructorName = async (instructorName, aType) => {
  try {
    const response = await http.get(
      `/api/ohioapproved/instructor_search?string=${instructorName}&aType=${aType}`,
    );
    return response.data;
  } catch (ex) {
    // Handle 404 as its a valid response if no items are found by the API.
    if (ex.response.status === 404) {
      return { users: [] };
    } else {
      console.log(ex);
    }
  }
};

export const getObjectives = async () => {
  try {
    const response = await http.get(`/tasc/get_objectives`);
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const searchTrainings = async (searchString, searchParameters) => {
  try {
    if (typeof searchString !== "string") {
      console.log("searchString must be a string");
      return;
    }
    const response = await http.get(`/api/training/lookup`, {
      params: { name: searchString, ...searchParameters },
    });
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const fetchTrainingAgeGroups = async (authorId) => {
  const url = !!authorId
    ? `/api/ohioapproved/age_groups/${authorId}`
    : `/api/ohioapproved/age_groups`;
  try {
    var { data } = await http.get(url);
    // Filter out age groups that require approval if authorId is not provided.
    data = authorId
      ? data.filter(
          (ageGroup) =>
            (ageGroup.approved && ageGroup.requires_approval === "1") ||
            ageGroup.requires_approval === "0",
        )
      : data.filter((ageGroup) => ageGroup.requires_approval === "1");
    return data;
  } catch (ex) {
    console.log(ex);
  }
};

export const createTraining = async (trainingOrgId, payload, legacy) => {
  let response;
  try {
    if (legacy) {
      // Legacy API
      const {
        data: { applicationId },
      } = await http.post(`/api/training/${trainingOrgId}`, payload);
      payload.id = applicationId; // The legacy api returns the applicationId so we will set it on the payload.
      await http.put(`/api/training`, payload);
      return payload;
    } else {
      // V2 API
      const response = await http.post(`/api/v2/training`, payload);
      const { data } = response;
      const { id, template_status, content_plan } = data;
      return { ...payload, id, template_status, content_plan };
    }
  } catch (ex) {
    console.log(ex);
    return ex?.response?.data;
  }
};

export const updateTraining = async (payload, legacy) => {
  try {
    if (legacy) {
      await http.put(`/api/training`, payload);
      return payload; // The legacy api returns "training update successful" so we will return the payload instead.
    } else {
      const response = await http.put(`/api/v2/training`, payload);
      return response.data;
    }
  } catch (ex) {
    console.log(ex);
  }
};

export const deleteTraining = async (eventId) => {
  try {
    const response = await http.delete(`/api/training/${eventId}`);
    return !!response;
  } catch (ex) {
    console.log(ex);
  }
};

export const loadBrightspaceOrgs = async () => {
  try {
    const response = await http.get(`/api/training/lms?type=brightspace`);
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const loadInsightOrgs = async () => {
  try {
    const response = await http.get(`/api/training/lms?type=insight`);
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const loadCoxCampusOrgs = async () => {
  try {
    const response = await http.get(`/api/training/lms?type=coxcampus`);
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const loadOHTrainOrgs = async () => {
  try {
    const response = await http.get(`/api/training/lms?type=ohtrain`);
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const loadInserviceTypes = async () => {
  try {
    const response = await http.get(`/api/training/inservice_types`);
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const getEventById = async (eventId, legacy = false) => {
  try {
    if (legacy) {
      const response = await http.get(`/api/training/${eventId}`);
      return response.data;
    } else {
      const response = await http.get(`/api/v2/training/${eventId}`);
      return response.data;
    }
  } catch (ex) {
    console.log(ex);
  }
};

export const getConfiguration = async () => {
  try {
    const response = await http.get(`/api/v2/training/config`);
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const getTrainingFindings = async () => {
  try {
    const response = await http.get(`/api/training/findings`);
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const getFunders = async (trainingOrgId) => {
  try {
    const response = await http.get(
      `/api/training_organization/${trainingOrgId}/funders`,
    );
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const getScheduledEventById = async (eventId) => {
  try {
    const response = await http.get(`/api/v2/scheduled_events/${eventId}`);
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const createOutline = async (trainingOrgId, contentPlanId, payload) => {
  try {
    const response = await http.post(
      `/api/v2/training/outline/${trainingOrgId}/${contentPlanId}`,
      payload,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return response.data;
  } catch (ex) {
    console.log(ex);
    throw ex;
  }
};

export const cancelScheduledEventById = async (eventId, payload) => {
  try {
    const response = await http.put(
      `/api/scheduled_events/${eventId}/cancel`,
      payload,
    );
    return response.data;
  } catch (ex) {
    console.log(ex);
    throw ex;
  }
};

export const completeScheduledEventById = async (eventId, payload) => {
  try {
    const response = await http.put(
      `/api/scheduled_events/${eventId}/complete`,
      payload,
    );
    return response.data;
  } catch (ex) {
    console.log(ex);
    throw ex;
  }
};

export const getApprovedTrainingInstructors = async (templateId) => {
  try {
    const response = await http.get(
      `/api/training_organization/instructors/approved?template_id=${templateId}`,
    );
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const uploadTrainingFile = async (trainingId, file) => {
  const formData = new FormData();
  formData.append("files", file);
  formData.append("app_id", trainingId);
  formData.append("file_type", "tasc");
  formData.append("section", "resume");
  formData.append("type", "resume");
  formData.append("name", file.name);
  formData.append("description", "a file for the training application");
  const response = await http.post(`/fileupload/application`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const getTrainingFiles = async (trainingId) => {
  try {
    const response = await http.post(`/tasc/get_files`, { id: trainingId });
    return response.data?.data || [];
  } catch (ex) {
    console.log(ex);
  }
};

export const removeTrainingFile = async (fileId) => {
  try {
    const formData = new FormData();
    formData.append("id", fileId);
    const response = await http.post(`/tasc/remove_file`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const getRegistrants = async ({
  eventId,
  firstName,
  lastName,
  opin,
  page,
  pageSize,
  sort,
  sortDir,
}) => {
  try {
    const formData = new FormData();
    formData.append("event_id", eventId);
    formData.append("first_name", firstName || "");
    formData.append("last_name", lastName || "");
    formData.append("opin", opin || "");
    formData.append("page", page + 1);
    formData.append("pageSize", pageSize);
    formData.append("sort", sort);
    formData.append("sort_direction", sortDir);
    const response = await http.post(`/events/search_registrants`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const removeRegistrant = async (registrantId, reason) => {
  try {
    const formData = new FormData();
    formData.append("id", registrantId);
    formData.append("reason", reason);
    const response = await http.post(`/events/cancel_registrant`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const searchRegistrants = async (query) => {
  try {
    const response = await http.post(`/tasc/search_author`, { string: query });
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const addRegistrant = async (eventId, registrantId) => {
  try {
    const formData = new FormData();
    formData.append("user_id", registrantId);
    formData.append("product_id", eventId);
    const response = await http.post(`/registration/add_registrant`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const uploadTrainingRegistration = async (
  scheduledEventId,
  sessionId,
  markAll,
  file,
) => {
  const formData = new FormData();
  formData.append("file", file, file.name);
  formData.append("mark_all_attended", markAll);
  const response = await http.post(
    `/api/scheduled_events/${scheduledEventId}/sessions/${sessionId}/upload_attendance`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return response.data;
};

export const getSessionAttendance = async (
  scheduledEventId,
  sessionId,
  page,
  pageSize,
  sortBy,
  sortDesc,
  filterModel,
) => {
  const columnFilters = [];
  if (filterModel && filterModel.items.length > 0) {
    for (const item of filterModel.items) {
      columnFilters.push({ id: item.field, value: item.value });
    }
  }
  const payload = {
    product_id: scheduledEventId,
    id: sessionId,
    page,
    pageSize,
    sortBy,
    sortDesc,
  };
  if (columnFilters.length > 0) {
    payload.columnFilters = columnFilters;
  }
  const response = await http.post(
    `/registration/get_session_attendance`,
    payload,
  );
  return response.data;
};

export const updateAttendance = async (
  scheduledEventId,
  sessionId,
  userId,
  attended,
  dateAttended,
) => {
  const payload = {
    date_attended: dateAttended
      ? dayjs(dateAttended).format("MM/DD/YYYY")
      : null,
    user_attendance: {
      [userId]: {
        update: {
          [sessionId]: Number(attended),
        },
      },
    },
    event_id: scheduledEventId,
    view_user_id: userId,
    session_id: sessionId,
  };
  const response = await http.post(
    `/events/update_attendance`,
    qs.stringify(payload),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
    },
  );
  return response.data;
};

export const addScheduledEvent = async (payload) => {
  const response = await http.post(`/api/v2/scheduled_events`, payload);
  return response.data;
};

export const updateScheduledEvent = async (payload) => {
  const response = await http.put(
    `/api/v2/scheduled_events/${payload.id}`,
    payload,
  );
  return response.data;
};

export const searchScheduledEvents = async (
  orgId,
  pageNumber,
  pageSize,
  sortBy,
  isDesc,
  filterModel,
  archived = false,
) => {
  try {
    let baseUrl = `/api/training_organization/scheduled_events/search?organization_id=${orgId}&page=${pageNumber}&pageSize=${pageSize}&sortBy=${sortBy}&sortDir=${
      isDesc ? "desc" : "asc"
    }`;
    const columnFilters = [];
    if (filterModel && filterModel.items.length > 0) {
      for (const item of filterModel.items) {
        let { field, value } = item;
        if (field === "start_date1") {
          field = "start_date";
        }
        columnFilters.push({ id: field, value });
      }
    }
    if (archived === 'active') {
      columnFilters.push({ id: "archived", value: "0" });
    } else if (archived === 'archived') {
      columnFilters.push({ id: "archived", value: "1" });
    }
    baseUrl += `&columnFilters=${JSON.stringify(columnFilters)}`;
    const response = await http.get(baseUrl);
    const { data, total } = response.data;

    return { data, total };
  } catch (ex) {
    console.log(ex);
  }
};

export const getScheduledEventsByEventId = async (eventId) => {
  try {
    const response = await http.get(`/api/training/events/${eventId}`);
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const deleteScheduledEvent = async (eventId) => {
  try {
    const response = await http.post(
      `/tasc/remove_event`,
      { id: eventId },
      {
        headers: {
          "Content-Type": `application/x-www-form-urlencoded;charset=UTF-8;`,
          Accept: "*/*",
          "X-Requested-With": "XMLHttpRequest",
        },
      },
    );
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const getWaitlist = async (eventId, firstName, opin) => {
  try {
    const formData = new FormData();
    formData.append("event_id", eventId);
    formData.append("first_name", firstName || "");
    formData.append("opin", opin || "");
    const response = await http.post(
      `/api/v2/events/waitlist/search`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return response.data.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const exportEvents = async (trainingOrgId) => {
  return http.post(
    `/reports/si_3_report?doc_name=training_org_all_events`,
    trainingOrgId,
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    },
  );
};

export const exportScheduledEvents = async (events) => {
  try {
    const response = await http.post(
      `/reports/si_1_report?doc_name=training_org_scheduled_events`,
      events,
    );
  } catch (ex) {
    console.log(ex);
  }
};

export const addTrainingToSeries = async (seriesId, trainingId) => {
  try {
    const response = await http.put(
      `/api/training/series_at/${seriesId}/${trainingId}`,
    );
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const removeFromWaitlist = async (eventId, registrantId) => {
  try {
    const response = await http.put(`/api/v2/events/waitlist/remove`, {
      user_id: registrantId,
      product_id: eventId,
    });
    return response.data;
  } catch (ex) {
    console.log(ex);
  }
};

export const getUserCounties = async (userId, oin, permissionId) => {
  try {
    const response = await http.get(
      `/api/v2/organization/${oin}/counties/${userId}/${permissionId}`
    );
    return response.data.user_counties ?? [];
  } catch (ex) {
    console.log(ex);
    throw ex;
  }
};

export const addUserCounty = async (userId, oin, permissionId, countyId) => {
  try {
    const response = await http.post(`/api/v2/organization/${oin}/counties`, {
      user_id: userId,
      permission_id: permissionId,
      county_id: countyId,
    });
    return response.data.user_counties ?? [];
  } catch (ex) {
    console.log(ex);
    throw ex;
  }
};

export const removeUserCounty = async (userId, oin, permissionId, countyId) => {
  try {
    const response = await http.delete(
      `/api/v2/organization/${oin}/counties/${userId}/${permissionId}/${countyId}`
    );
    return response.data.user_counties ?? [];
  } catch (ex) {
    console.log(ex);
    throw ex;
  }
};
